import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { NAME, ONBOARDING_STEPS } from './constants';

import type { IOnboarding, IRegistrationOnboarding, UserOnboardingData, OnboardingStep } from './types';

// Combined state interface
interface OnboardingState {
  // Journey/tooltip onboarding
  journey: IOnboarding;
  // Registration flow onboarding
  registration: IRegistrationOnboarding;
}

const initialState: OnboardingState = {
  journey: {
    currentJourney: null,
    currentStep: null,
    previousJourneys: [],
    callbackTriggerId: null,
  },
  registration: {
    currentStep: ONBOARDING_STEPS.NAME as OnboardingStep,
    userData: {},
    isLoading: false,
    error: null,
    progress: 0,
  },
};

export const onboardingSlice = createSlice({
  name: NAME,
  initialState,
  reducers: {
    // Journey/tooltip onboarding actions
    setCurrentJourney: (state, { payload }: PayloadAction<IOnboarding['currentJourney']>) => {
      if (!payload || !state.journey.previousJourneys.includes(payload)) {
        state.journey.currentJourney = payload;
      }
    },
    setCurrentStep: (state, { payload }: PayloadAction<IOnboarding['currentStep']>) => {
      if (!payload || state.journey.currentJourney) {
        state.journey.currentStep = payload;
      }
    },
    setCallbackTriggerId: (state, { payload }: PayloadAction<IOnboarding['callbackTriggerId']>) => {
      if (!payload || (state.journey.currentJourney && state.journey.currentStep)) {
        state.journey.callbackTriggerId = payload;
      }
    },
    resetState: state => {
      if (state.journey.currentJourney) state.journey.previousJourneys.push(state.journey.currentJourney);
      state.journey.currentJourney = initialState.journey.currentJourney;
      state.journey.currentStep = initialState.journey.currentStep;
      state.journey.callbackTriggerId = initialState.journey.callbackTriggerId;
    },
    
    // Registration onboarding actions
    setRegistrationStep: (state, { payload }: PayloadAction<OnboardingStep>) => {
      state.registration.currentStep = payload;
      // Update progress based on step
      const stepProgress = {
        [ONBOARDING_STEPS.NAME]: 0.25,
        [ONBOARDING_STEPS.INDUSTRY]: 0.5,
        [ONBOARDING_STEPS.ROLE]: 0.75,
        [ONBOARDING_STEPS.EMAIL]: 1.0,
        [ONBOARDING_STEPS.CREATING_ACCOUNT]: 1.0,
      };
      state.registration.progress = stepProgress[payload] || 0;
    },
    setUserData: (state, { payload }: PayloadAction<Partial<UserOnboardingData>>) => {
      state.registration.userData = {
        ...state.registration.userData,
        ...payload,
      };
    },
    setRegistrationLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.registration.isLoading = payload;
    },
    setRegistrationError: (state, { payload }: PayloadAction<string | null>) => {
      state.registration.error = payload;
    },
    resetRegistration: state => {
      state.registration = initialState.registration;
    },
  },
});

export const { 
  setCurrentJourney, 
  setCurrentStep, 
  setCallbackTriggerId, 
  resetState,
  setRegistrationStep,
  setUserData,
  setRegistrationLoading,
  setRegistrationError,
  resetRegistration,
} = onboardingSlice.actions;
